import React from 'react';

/* Shared building blocks for the fresh road demo UI */

export const Card = ({ children, className = '' }) => (
  <div className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm ${className}`}>{children}</div>
);

export const Badge = ({ children, tone = 'gray' }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-sky-50 text-sky-700',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-violet-50 text-violet-700',
    teal: 'bg-teal-50 text-teal-700',
    gray: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const StatusDot = ({ tone = 'green', label }) => {
  const dots = {
    green: 'bg-emerald-500',
    blue: 'bg-sky-500',
    orange: 'bg-orange-400',
    gray: 'bg-slate-300',
  };
  const text = {
    green: 'text-emerald-700 bg-emerald-50',
    blue: 'text-sky-700 bg-sky-50',
    orange: 'text-orange-600 bg-orange-50',
    gray: 'text-slate-500 bg-slate-100',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium ${text[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dots[tone]}`} />
      {label}
    </span>
  );
};

export const Toggle = ({ on, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onChange}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
      on ? 'bg-teal-600' : 'bg-slate-200'
    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
        on ? 'left-[22px]' : 'left-0.5'
      }`}
    />
  </button>
);

export const PrimaryButton = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-[14px] font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const PageTitle = ({ title, subtitle, action, icon: Icon }) => (
  <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 pb-4 pt-4">
    <div className="flex items-center gap-3.5">
      {Icon ? (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
          <Icon size={22} strokeWidth={1.9} />
        </span>
      ) : (
        <span className="h-9 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-teal-500 to-emerald-500" />
      )}
      <div className="min-w-0">
        <h1 className="text-[24px] font-bold leading-tight tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[13.5px] text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);
